import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { Patient } from "@/Common/Models/Patient/Patient";
import { PhoneNumberHelper } from "@/ContactDirectory/Helpers/PhoneNumberHelper";
import { IconBadge } from "@/DesignSystem/Molecules/IconBadge/IconBadge";
import { ClickToDialViewMode } from "../CallCenter/ClickToDial.types";
import { ClickToDialViewPhoneNumber } from "../CallCenter/ClickToDialViewPhoneNumber";

interface PatientContactDisplayProps {
    patient: Patient;
    shouldRenderAllContactTypes?: boolean;
    sendToAllPersonalContact: boolean;
    isNotificationSet: boolean;
    showNotificationCheckbox: boolean;
    showOnlyCellPhonesAndEmails?: boolean;
    contactPointNotificationIds: string[];
    hasSmsNotificationsEnabled: boolean;
    hasEmailNotificationsEnabled: boolean;
    onSendToAllPersonalContactChange: (checked: boolean) => void;
    onPhoneNotificationChange: (checked: boolean, item: PhoneNumber) => void;
    onEmailNotificationChange: (checked: boolean, item: Email) => void;
    onRepresentativePhoneNotificationChange: (checked: boolean, item: PhoneNumber) => void;
    onRepresentativeEmailNotificationChange: (checked: boolean, item: Email) => void;
}
class PatientContactDisplay extends BaseReactComponent<PatientContactDisplayProps> {
    public render(): JSX.Element {
        const phones = this.getSortedPhones().map(cell => this.renderPhoneDisplay(cell, false, "", this.props.patient));

        return (
            <div>
                <div className="grayunderline patient" style={{ marginBottom: "10px" }}>
                    <span className="graytext">{cacheStore.GetString("ContactLabel")}</span>
                </div>
                <div>
                    {this.showSendNotificationsToAllPersonalContacts && (
                        <div style={{ marginBottom: "5px" }}>
                            <CheckboxControl
                                checked={this.props.sendToAllPersonalContact}
                                label={cacheStore.GetString("SendNotificationsToAllPersonalContactLabel")}
                                onChange={checked => this.props.onSendToAllPersonalContactChange(checked)}
                            />
                        </div>
                    )}
                    {this.representativeDisplay()}
                    {phones?.any()
                        && (
                            <label
                                className="bold"
                                data-cy="patient-appointment-contact-phone"
                            >
                                {cacheStore.GetString("TelephoneLabel")}
                            </label>
                        )
                    }
                    {phones}
                    {(this.props.patient.IndividualResponse.DoNotEmail || this.props.patient.Emails?.any())
                        && <label className="bold">{cacheStore.GetString("EmailLabel")}</label>
                    }
                    {this.props.patient.IndividualResponse.DoNotEmail
                        ? this.renderDoNotEmail()
                        : this.props.patient.Emails?.map(email => this.renderEmailDisplay(email))
                    }
                </div>
            </div>
        );
    }

    private renderDoNotEmail(): JSX.Element {
        return <div style={{ marginBottom: "5px" }}>{cacheStore.GetString("DoNotEmailLabel")}</div>;
    }

    private representativeDisplay(): React.ReactNode {
        const patient = this.props.patient;

        const mobilePhoneType = cacheStore.getLookupByPhoneType(PhoneType.Mobile).LookupID;

        const representative = patient?.Relationships?.filter(p => p.IsRepresentative);
        if (!representative?.any())
            return null;

        const toReturn: JSX.Element[] = [];

        representative
            .forEach(p => {
                const emails = p.Emails
                    .map(e => this.renderEmailDisplay(e, true, `${cacheStore.GetLookupById(p.RelationLookupId).GetValue()} - `));
                if (emails.any() && cacheStore.portalConfiguration?.HasEmailNotificationEnabled)
                    toReturn.push(...emails);

                const phones = p.PhoneNumbers
                    .filter(p => p.PhoneTypeLookupId === mobilePhoneType)
                    .map(e => this.renderPhoneDisplay(e, true, `${cacheStore.GetLookupById(p.RelationLookupId).GetValue()} - `));
                if (cacheStore.portalConfiguration?.HasSmsNotificationEnabled && phones.any())
                    toReturn.push(...phones);
            });

        return toReturn?.any() && (
            <div>
                <label className="bold">{cacheStore.GetString("RepresentativeLabel")}</label>
                {toReturn}
            </div>
        );
    }

    private renderPhoneDisplay(phone: PhoneNumber, isRepresentative: boolean = false, prefix: string = "", patient: Patient = null): JSX.Element {
        const callCenterFeatureFlag: boolean = cacheStore.ClinicSettings.IsCallCenterEnabled();
        let phoneNumberControl = null;

        if (callCenterFeatureFlag) {
            phoneNumberControl = <ClickToDialViewPhoneNumber viewMode={ClickToDialViewMode.Hyperlink} phoneNumber={phone} patientContactFacetId={this.props.patient?.PatientResponse?.ContactFacet_id} />;
        }
        else {
            phoneNumberControl = <>{PhoneNumber.stringFormattedPhone(phone, true)}</>;
        }

        const isCellPhone = phone.PhoneTypeLookupId == cacheStore.getLookupByPhoneType(PhoneType.Mobile)?.LookupID;
        let phoneIcon = null;

        if (patient)
            phoneIcon = PhoneNumberHelper.getPhoneIconVariantByPatient(phone, patient);

        return (
            <div key={phone.ContactPoint_id} style={{ marginBottom: "5px" }}>
                <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                    {(this.props.hasSmsNotificationsEnabled && isCellPhone) && this.showNotificationCheckbox() && (
                        <CheckboxControl
                            id={`contactPoint_cell_${phone.ContactPoint_id}`}
                            style={{ marginRight: "5px", marginTop: "0px" }}
                            labelStyle={{ margin: 0 }}
                            checked={this.props.contactPointNotificationIds?.contains(phone.ContactPoint_id)}
                            onChange={checked => (isRepresentative ? this.props.onRepresentativePhoneNotificationChange : this.props.onPhoneNotificationChange)(checked, phone)}
                        />
                    )}
                    <label className="margin-none" htmlFor={`contactPoint_cell_${phone.ContactPoint_id}`}>{phoneNumberControl}</label>
                    <span
                        className="form-subtext"
                        style={{ marginLeft: "5px" }}
                        data-cy={PhoneNumberHelper.validateWalkMePhoneNumber(phone.ContactPointTypeLookupId, phone.PhoneTypeLookupId)
                            ? "patient-contact-display-phone-type"
                            : undefined
                        }
                    >
                        {`${prefix}${MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(phone.ContactPointTypeLookupId))} - ${MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(phone.PhoneTypeLookupId))}`}
                    </span>
                    {phoneIcon && (
                        <span title={cacheStore.GetString(phoneIcon.tooltipLocalizationKey)} className="margin-xs-left">
                            <IconBadge size="sm" variant={phoneIcon.variant} />
                        </span>
                    )}

                </div>
                {!IsNullOrWhiteSpace(phone.Notes) && <p>{phone.Notes}</p>}
            </div>
        );
    }

    private renderEmailDisplay(email: Email, isRepresentative: boolean = false, prefix: string = ""): JSX.Element {
        const checkboxId = `EmailNotification_${email.Address}`;
        return (
            <div key={email.ContactPoint_id} style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", marginBottom: "5px" }}>
                {this.props.hasEmailNotificationsEnabled && this.showNotificationCheckbox() && (
                    <CheckboxControl
                        id={checkboxId}
                        style={{ marginRight: "5px", marginTop: "0px" }}
                        labelStyle={{ margin: 0 }}
                        checked={this.props.contactPointNotificationIds?.contains(email.ContactPoint_id)}
                        onChange={checked => (isRepresentative ? this.props.onRepresentativeEmailNotificationChange : this.props.onEmailNotificationChange)(checked, email)}
                    />
                )}
                <span><a href={"mailto:" + email.Address}>{email.Address}</a></span>
                <span className="form-subtext" style={{ marginLeft: "5px" }}>
                    ({`${prefix}${MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(email.ContactPointTypeLookupId))}`})
                </span>
            </div>
        );
    }

    private showNotificationCheckbox(): boolean {
        return (
            this.props.showNotificationCheckbox
            && (
                this.props.isNotificationSet
                || this.props.hasEmailNotificationsEnabled
                || this.props.hasSmsNotificationsEnabled
            )
        );
    }

    private get showSendNotificationsToAllPersonalContacts(): boolean {
        return this.props.patient.hasNotifiableContactPoints
            && this.props.isNotificationSet
            && !this.props.contactPointNotificationIds?.any()
            && (this.props.hasSmsNotificationsEnabled || this.props.hasEmailNotificationsEnabled);
    }

    private getSortedPhones(): PhoneNumber[] {
        const cellType = cacheStore.FindLookupByLookupCode("MOBILE", "PHONETYPE");

        const patientPhones = this.props.showOnlyCellPhonesAndEmails
            ? this.props.patient.Phones.filter(p => p.PhoneTypeLookupId === cellType.LookupID)
            : this.props.patient.Phones;

        const phones = patientPhones?.filter(p => p.Number != "0"); // ignore invalid numbers from interfaces
        const preferred = phones?.filter(p => p.IsPreferred);
        const otherContactPointLookupId = cacheStore.getLookupByContactPointType(ContactPointType.Other).LookupID;

        let others = phones?.filter(p => !p.IsPreferred && (this.props.shouldRenderAllContactTypes || p.ContactPointTypeLookupId !== otherContactPointLookupId));

        const phoneNumberDisplay = (preferred ?? []);

        // display the other phone numbers in that order : home, work, other, then CEL,PHONE,PAGER,FAX... never display 'other' unless it is chosen as preferred
        if (others?.length) {
            const workType = cacheStore.FindLookupByLookupCode("WORK", "CONTACTPOINTTYPE");
            const personalType = cacheStore.FindLookupByLookupCode("PERSONAL", "CONTACTPOINTTYPE");
            const phoneType = cacheStore.FindLookupByLookupCode("PHONE", "PHONETYPE");
            const pagerType = cacheStore.FindLookupByLookupCode("PAGER", "PHONETYPE");
            const faxType = cacheStore.FindLookupByLookupCode("FAX", "PHONETYPE");

            for (let otherPhone of others) {
                let sortKey = "";
                switch (otherPhone.ContactPointTypeLookupId) {
                    case personalType.LookupID:
                        sortKey = "A";
                        break;
                    case workType.LookupID:
                        sortKey = "B";
                        break;
                    default:
                        sortKey = "Z";
                }

                switch (otherPhone.PhoneTypeLookupId) {
                    case cellType.LookupID:
                        sortKey += "A";
                        break;
                    case phoneType.LookupID:
                        sortKey += "B";
                        break;
                    case pagerType.LookupID:
                        sortKey += "C";
                        break;
                    case faxType.LookupID:
                        sortKey += "D";
                        break;
                    default:
                        sortKey += "Z";
                }

                otherPhone.SortingValue = sortKey;
            }
            others = others.sortByProperty(p => p.SortingValue);

            for (let phone of others)
                phoneNumberDisplay.push(phone);
        }

        return phoneNumberDisplay;
    }
}

export { PatientContactDisplay };
